import api from './api';
import { User, PaginationParams, ApiResponse } from '../types';

export interface CreateUserRequest {
  nome: string;
  nome_resumido?: string | null;
  usuario: string;
  email: string;
  senha: string;
  perfil: 'administrador' | 'financeiro' | 'suporte' | 'bot';
  telefone?: string;
  funcionario_id?: number | null;
  escritorio_ids?: number[];
  grupo_id?: number | null;
    permissoes_custom?: {
    grants?: string[];
    denies?: string[];
  } | null;
  sigame?: boolean;
  ticket_novo?: boolean;
  telefones_sigame?: string | null;
  sigame_hora_inicio?: string | null;
  sigame_hora_fim?: string | null;
}

export interface UpdateUserRequest {
  nome?: string;
  nome_resumido?: string | null;
  usuario?: string;
  email?: string;
  senha?: string;
  perfil?: 'administrador' | 'financeiro' | 'suporte' | 'bot';
  telefone?: string;
  funcionario_id?: number | null;
  escritorio_ids?: number[];
  grupo_id?: number | null;
    permissoes_custom?: {
    grants?: string[];
    denies?: string[];
  } | null;
  sigame?: boolean;
  ticket_novo?: boolean;
  telefones_sigame?: string | null;
  sigame_hora_inicio?: string | null;
  sigame_hora_fim?: string | null;
}

export interface ChangePasswordRequest {
  senhaAtual: string;
  novaSenha: string;
}

export interface UsersResponse {
  users: User[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

class UserService {
  /**
   * Lista todos os usuários com paginação
   */
  async getAllUsers(params?: PaginationParams): Promise<UsersResponse> {
    try {
      const queryParams = new URLSearchParams();
      
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      if (params?.search) queryParams.append('search', params.search);

      const url = `/users?${queryParams.toString()}`;

      const response = await api.get<ApiResponse<User[]> | User[]>(url);
      
      // Verificar se a resposta é um array direto ou objeto estruturado
      if (Array.isArray(response.data)) {
        // Resposta é array direto - criar estrutura esperada
        const result = {
          users: response.data,
          pagination: {
            page: params?.page || 1,
            limit: params?.limit || 10,
            total: response.data.length,
            pages: 1,
          },
        };
        
        return result;
      }
      
      // Resposta é objeto estruturado
      const apiResponse = response.data as ApiResponse<User[]>;
      if (apiResponse.success && apiResponse.data) {
        const result = {
          users: apiResponse.data,
          pagination: apiResponse.pagination || {
            page: 1,
            limit: 10,
            total: apiResponse.data.length,
            pages: 1,
          },
        };
        
        return result;
      }
      
      throw new Error(apiResponse.message || 'Erro ao buscar usuários');
    } catch (error: any) {
      
      // Se for erro de rede ou timeout, dar mensagem específica
      if (error.code === 'NETWORK_ERROR' || error.message.includes('timeout')) {
        throw new Error('Erro de conexão com o servidor');
      }
      
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Erro interno do servidor'
      );
    }
  }

  /**
   * Busca um usuário específico por ID
   */
  async getUserById(id: number): Promise<User> {
    try {
      const response = await api.get<ApiResponse<User>>(`/users/${id}`) as unknown as ApiResponse<User>;
      
      // Como o interceptor retorna response.data diretamente,
      // response agora já é o objeto { success, data, message }
      if (response.success && response.data) {
        return response.data;
      }
      
      throw new Error(response.message || 'Usuário não encontrado');
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Erro interno do servidor'
      );
    }
  }

  /**
   * Cria um novo usuário
   */
  async createUser(userData: CreateUserRequest): Promise<User> {
    try {
      // Validar dados obrigatórios
      if (!userData.nome || !userData.usuario || !userData.email || !userData.senha) {
        throw new Error('Nome, usuário, email e senha são obrigatórios');
      }

      const response = await api.post<ApiResponse<User>>('/users', userData) as unknown as ApiResponse<User>;
      
      // Como o interceptor retorna response.data diretamente,
      // response agora já é o objeto { success, data, message }
      if (response.success && response.data) {
        return response.data;
      }
      
      throw new Error(response.message || 'Erro ao criar usuário');
    } catch (error: any) {
      // Tratar erros específicos
      if (error.response?.status === 409) {
        throw new Error('Email ou nome de usuário já cadastrado');
      }
      
      // Tratar erros de validação (400)
      if (error.response?.status === 400 && error.response?.data?.errors) {
        const errors = error.response.data.errors;
        // Se errors é um array de objetos com propriedade 'msg', extrair as mensagens
        if (Array.isArray(errors) && errors.length > 0) {
          const errorMessages = errors.map((err: any) => 
            typeof err === 'string' ? err : (err.msg || err.message || JSON.stringify(err))
          ).join(', ');
          throw new Error(errorMessages);
        }
      }
      
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Erro interno do servidor'
      );
    }
  }

  /**
   * Atualiza um usuário existente
   */
  async updateUser(id: number, userData: UpdateUserRequest): Promise<User> {
    try {
      const response = await api.put<ApiResponse<User>>(`/users/${id}`, userData) as unknown as ApiResponse<User>;
      
      // Como o interceptor retorna response.data diretamente,
      // response agora já é o objeto { success, data, message }
      if (response.success && response.data) {
        return response.data;
      }
      
      throw new Error(response.message || 'Erro ao atualizar usuário');
    } catch (error: any) {
      // Tratar erros específicos
      if (error.response?.status === 404) {
        throw new Error('Usuário não encontrado');
      }
      
      if (error.response?.status === 409) {
        throw new Error('Email ou nome de usuário já está em uso');
      }
      
      // Tratar erros de validação (400)
      if (error.response?.status === 400 && error.response?.data?.errors) {
        const errors = error.response.data.errors;
        // Se errors é um array de objetos com propriedade 'msg', extrair as mensagens
        if (Array.isArray(errors) && errors.length > 0) {
          const errorMessages = errors.map((err: any) => 
            typeof err === 'string' ? err : (err.msg || err.message || JSON.stringify(err))
          ).join(', ');
          throw new Error(errorMessages);
        }
      }
      
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Erro interno do servidor'
      );
    }
  }

  /**
   * Remove um usuário (exclusão lógica)
   */
  async deleteUser(id: number): Promise<void> {
    try {
      const response = await api.delete<ApiResponse<void>>(`/users/${id}`) as unknown as ApiResponse<void>;
      
      // Como o interceptor retorna response.data diretamente,
      // response agora já é o objeto { success, data, message }
      if (!response.success) {
        throw new Error(response.message || 'Erro ao excluir usuário');
      }
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Usuário não encontrado');
      }
      
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Erro interno do servidor'
      );
    }
  }

  /**
   * Alterna o status de um usuário (ativo/inativo)
   */
  async toggleUserStatus(id: number): Promise<User> {
    try {
      const response = await api.patch<ApiResponse<User>>(`/users/${id}/toggle-status`) as unknown as ApiResponse<User>;
      
      if (response.success && response.data) {
        return response.data;
      }
      
      throw new Error(response.message || 'Erro ao alterar status do usuário');
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Usuário não encontrado');
      }
      
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Erro interno do servidor'
      );
    }
  }

  /**
   * Altera a senha de um usuário (apenas admin)
   */
  async changePassword(id: number, passwordData: ChangePasswordRequest): Promise<void> {
    try {
      const response = await api.patch<ApiResponse<void>>(`/users/${id}/change-password`, passwordData) as unknown as ApiResponse<void>;
      
      // Como o interceptor retorna response.data diretamente,
      // response agora já é o objeto { success, data, message }
      if (!response.success) {
        throw new Error(response.message || 'Erro ao alterar senha');
      }
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Usuário não encontrado');
      }
      
      // Tratar erros de validação (400)
      if (error.response?.status === 400 && error.response?.data?.errors) {
        const errors = error.response.data.errors;
        // Se errors é um array de objetos com propriedade 'msg', extrair as mensagens
        if (Array.isArray(errors) && errors.length > 0) {
          const errorMessages = errors.map((err: any) => 
            typeof err === 'string' ? err : (err.msg || err.message || JSON.stringify(err))
          ).join(', ');
          throw new Error(errorMessages);
        }
      }
      
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Erro interno do servidor'
      );
    }
  }

  /**
   * Busca usuários por perfil
   */
  async getUsersByRole(perfil: string): Promise<User[]> {
    try {
      const response = await api.get<ApiResponse<User[]>>(`/users/role/${perfil}`) as unknown as ApiResponse<User[]>;
      
      // Como o interceptor retorna response.data diretamente,
      // response agora já é o objeto { success, data, message }
      if (response.success && response.data) {
        return response.data;
      }
      
      throw new Error(response.message || 'Erro ao buscar usuários por perfil');
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Erro interno do servidor'
      );
    }
  }

  /**
   * Valida se um email está disponível
   */
  async isEmailAvailable(email: string, excludeId?: number): Promise<boolean> {
    try {
      const users = await this.getAllUsers();
      return !users.users.some(user => 
        user.email === email && (!excludeId || user.id !== excludeId)
      );
    } catch {
      return true; // Em caso de erro, assumir que está disponível
    }
  }

  /**
   * Valida se um nome de usuário está disponível
   */
  async isUsernameAvailable(usuario: string, excludeId?: number): Promise<boolean> {
    try {
      const users = await this.getAllUsers();
      return !users.users.some(user => 
        user.usuario === usuario && (!excludeId || user.id !== excludeId)
      );
    } catch {
      return true; // Em caso de erro, assumir que está disponível
    }
  }
}

// Instância única do serviço
const userService = new UserService();
export default userService; 
